// Automatic marketplace when a business profile has no URL intent and no saved default
// that still exists. Candidates are the Ads leaves the dropdown actually offers
// (buildMarketplaceTree already drops attribution, agency, and seller-only rows).
//
// Tier 1 ranks marketplace-overview spend. Tier 2, only when nobody spent, counts
// ENABLED campaigns. Fewer than two candidates returns null and makes no request.
// The caller owns the time budget and passes an AbortSignal.

import dayjs from 'dayjs';

import { buildMarketplaceTree } from '@/helpers/marketplaceUnion.js';

const CAMPAIGN_CONCURRENCY = 4;

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const isAbortError = (error) =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED';

const abortError = () => {
  const error = new Error('Aborted');
  error.name = 'AbortError';
  return error;
};

// Ads leaves in dropdown order (the order findDefaultMarketplace walks).
export const selectableAdsCandidates = (profile) =>
  [...buildMarketplaceTree(profile).leafByUid.values()]
    .filter((leaf) => leaf.profileId)
    .map((leaf, order) => ({
      leaf,
      order,
      profileId: leaf.profileId,
      sellerName: leaf.sellerName,
      syncedConfiguration: leaf.syncedConfiguration,
    }));

// findDefaultMarketplace order, then the lower profileId so a tie is deterministic.
const tieTier = (candidate) => {
  if (candidate.sellerName?.trim()) return 0;
  if (candidate.syncedConfiguration) return 1;
  return 2;
};

const compareTie = (a, b) => {
  const tierDiff = tieTier(a) - tieTier(b);
  if (tierDiff) return tierDiff;
  if (a.order !== b.order) return a.order - b.order;
  return Number(a.profileId) - Number(b.profileId);
};

const overviewWindow = (now) => {
  const today = dayjs(now);
  const currentStart = today.subtract(29, 'day');
  const previousEnd = currentStart.subtract(1, 'day');
  const previousStart = previousEnd.subtract(29, 'day');
  const format = (date) => date.format('YYYY-MM-DD');
  return {
    startDate: format(currentStart),
    endDate: format(today),
    previousStartDate: format(previousStart),
    previousEndDate: format(previousEnd),
    targetCurrency: 'USD',
  };
};

const rowsByProfileId = (marketplaces) => {
  const byId = new Map();
  for (const row of marketplaces || []) {
    if (row?.profileId == null) continue;
    byId.set(String(row.profileId), row);
  }
  return byId;
};

const spendMetrics = (row) => ({
  currentSpend: num(row?.current?.spend),
  previousSpend: num(row?.previous?.spend),
  fxMissing: row?.fxMissing === true,
  nativeSpend: num(row?.current?.spendNative) > 0 || num(row?.previous?.spendNative) > 0,
});

const qualifiesSpend = (metrics) => {
  if (metrics.currentSpend > 0 || metrics.previousSpend > 0) return true;
  return metrics.fxMissing && metrics.nativeSpend;
};

// Converted rows first. Inside a bucket: current spend, then previous spend, then the tie-break.
const compareSpend = (a, b) => {
  if (a.fxMissing !== b.fxMissing) return a.fxMissing ? 1 : -1;
  if (a.currentSpend !== b.currentSpend) return b.currentSpend - a.currentSpend;
  if (a.previousSpend !== b.previousSpend) return b.previousSpend - a.previousSpend;
  return compareTie(a, b);
};

const compareCount = (a, b) => {
  if (a.count !== b.count) return b.count - a.count;
  return compareTie(a, b);
};

const mapPool = async (items, limit, fn) => {
  const results = new Array(items.length);
  let cursor = 0;
  const workers = Math.min(limit, items.length);
  const worker = async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await fn(items[index]);
    }
  };
  await Promise.all(Array.from({ length: workers }, () => worker()));
  return results;
};

const rankBySpend = (candidates, marketplaces) => {
  const byId = rowsByProfileId(marketplaces);
  const scored = [];
  for (const candidate of candidates) {
    const row = byId.get(String(candidate.profileId));
    if (!row) continue;
    const metrics = spendMetrics(row);
    if (!qualifiesSpend(metrics)) continue;
    scored.push({ ...candidate, ...metrics });
  }
  if (!scored.length) return null;
  scored.sort(compareSpend);
  return { marketplace: scored[0].leaf, source: 'spend' };
};

const rankByCampaigns = async (profile, candidates, listCampaigns, signal) => {
  let countFailed = false;
  const counted = await mapPool(candidates, CAMPAIGN_CONCURRENCY, async (candidate) => {
    if (signal?.aborted) throw abortError();
    try {
      const res = await listCampaigns(
        { states: ['ENABLED'], maxResults: 1 },
        { businessProfileId: profile.id, profileId: candidate.profileId },
      );
      return { ...candidate, count: num(res?.data?.metadata?.total_count) };
    } catch (error) {
      if (signal?.aborted || isAbortError(error)) throw error;
      countFailed = true;
      return { ...candidate, count: 0 };
    }
  });
  if (signal?.aborted) throw abortError();
  const positive = counted.filter((candidate) => candidate.count > 0);
  if (!positive.length) return { marketplace: null, source: null, countFailed };
  positive.sort(compareCount);
  return { marketplace: positive[0].leaf, source: 'campaign' };
};

/**
 * A pick is `{ marketplace, source }`. No pick is `null` when fewer than two candidates exist,
 * or `{ marketplace: null, source: null, failed }` once both tiers ran: `failed` is true when
 * an error (not an empty answer) hid part of the data, so the caller must not cache the miss.
 * @returns {Promise<{ marketplace: object, source: 'spend' | 'campaign' }
 *   | { marketplace: null, source: null, failed: boolean } | null>}
 */
export const pickAutoDefaultMarketplace = async (profile, options = {}) => {
  const candidates = selectableAdsCandidates(profile);
  if (candidates.length < 2) return null;

  const { getOverview, listCampaigns, signal, now = new Date() } = options;
  if (typeof getOverview !== 'function' || typeof listCampaigns !== 'function') {
    throw new Error('pickAutoDefaultMarketplace requires getOverview and listCampaigns');
  }

  let marketplaces = [];
  let overviewFailed = false;
  try {
    const data = await getOverview(overviewWindow(now), signal, {
      headers: { businessProfileId: profile?.id },
    });
    marketplaces = data?.marketplaces || [];
  } catch (error) {
    if (signal?.aborted || isAbortError(error)) throw error;
    overviewFailed = true;
    console.warn('[marketplaceAutoDefault] marketplace overview failed:', error);
  }

  const bySpend = rankBySpend(candidates, marketplaces);
  if (bySpend) return bySpend;
  if (signal?.aborted) throw abortError();
  const byCampaigns = await rankByCampaigns(profile, candidates, listCampaigns, signal);
  if (byCampaigns.marketplace) return byCampaigns;
  return {
    marketplace: null,
    source: null,
    failed: overviewFailed || byCampaigns.countFailed,
  };
};
